import styled from 'styled-components';
import { Emoji } from '@frameio/shared/emoji';
import { GLYPHS } from '@frameio/utils/constants';
import type {
  ChipColors,
  ThemeColorsBackground,
  ThemeColorsMeta,
} from '@frameio/vapor';
import {
  Avatar,
  AvatarGroup,
  Box,
  Chip,
  Text,
  theme,
  Tooltip,
  whenFocused,
} from '@frameio/vapor';
import {
  IconExpand,
  IconFullscreenDefault,
  IconInfo,
} from '@frameio/vapor-icons';
import { type InputToken, InputTokenStatus } from '../../utils';
import TEXT from './generated/TEXT/UserInputToken';
import styles from './UserInputToken.module.css';

TEXT.DEFINE({
  /** Guest label */
  guest: 'Guest',
  /** New label */
  new: 'New',
  /** Member label */
  member: 'Member',
  /** Content Admin label */
  contentAdmin: 'Content Admin',
  /** Owner label */
  owner: 'Owner',
});

type ChipProps = React.ComponentProps<typeof Chip>;
export type UserInputTokenProps = InputToken & ChipProps;

const NOTICE_STATUSES = [
  InputTokenStatus.EXISTING_GROUP,
  InputTokenStatus.EXISTING_GUEST,
  InputTokenStatus.EXISTING_MEMBER,
  InputTokenStatus.DOWNGRADING_ADMIN,
  InputTokenStatus.DOWNGRADING_OWNER,
  InputTokenStatus.NEW_MEMBER_WARN,
];

const STATUS_TEXT = {
  [InputTokenStatus.EXISTING_GROUP]: (count?: number) =>
    `${count} ${TEXT.member}${count === 1 ? '' : 's'}`,
  [InputTokenStatus.EXISTING_GUEST]: () => TEXT.guest,
  [InputTokenStatus.EXISTING_MEMBER]: () => TEXT.member,
  [InputTokenStatus.GROUP]: () => null,
  [InputTokenStatus.INVALID_EMAIL]: () => null,
  [InputTokenStatus.MEMBER]: () => TEXT.member,
  [InputTokenStatus.NEW_GUEST]: () => TEXT.new,
  [InputTokenStatus.NEW_MEMBER]: () => TEXT.new,
  [InputTokenStatus.NEW_MEMBER_WARN]: () => TEXT.new,
  [InputTokenStatus.DOWNGRADING_ADMIN]: () => TEXT.contentAdmin,
  [InputTokenStatus.DOWNGRADING_OWNER]: () => TEXT.owner,
};

const TOKEN_COLORS: Record<string, ChipColors> = {
  DEFAULT: 'default',
  INVALID: 'red',
  NOTICE: 'yellow',
};

const StatusText = ({
  status,
  memberCount,
}: {
  status: InputTokenStatus;
  memberCount?: number;
}) => {
  if (
    [
      InputTokenStatus.GROUP,
      InputTokenStatus.MEMBER,
      InputTokenStatus.INVALID_EMAIL,
    ].includes(status)
  ) {
    return null;
  }

  const colorMap = {
    [InputTokenStatus.EXISTING_GUEST]: 'secondary',
    [InputTokenStatus.EXISTING_MEMBER]: 'secondary',
    [InputTokenStatus.NEW_GUEST]: 'tertiary',
    [InputTokenStatus.NEW_MEMBER]: 'informative',
    [InputTokenStatus.NEW_MEMBER_WARN]: 'tertiary',
    [InputTokenStatus.EXISTING_GROUP]: 'secondary',
    [InputTokenStatus.NEW_REVIEWER]: 'informative',
  };

  const statusText = STATUS_TEXT[status];
  return (
    <Text color={colorMap[status]} display="inline" variant="caption">
      {statusText && (
        <>
          <Box display="inline">{`${GLYPHS.nbsp}${GLYPHS.bullet}${GLYPHS.nbsp}`}</Box>
          {statusText(memberCount)}
        </>
      )}
    </Text>
  );
};

type TokenAvatarSlotProps = {
  avatarBg?: string;
  avatarNames?: string[];
  avatarSrc?: string | string[];
  emoji?: string;
  isClickable?: boolean;
  label: string;
};

function TokenAvatarSlot({
  avatarBg,
  avatarNames,
  avatarSrc,
  emoji,
  isClickable,
  label,
}: TokenAvatarSlotProps) {
  const showsPair = Array.isArray(avatarSrc) && avatarSrc.length >= 2;
  const hasExpandOverlay = Boolean(isClickable);
  const isStacked = showsPair || hasExpandOverlay;
  const avatarSize = isStacked ? 20 : 24;

  const identityAvatars = showsPair
    ? [
        <Avatar
          key="first"
          alt={avatarNames?.[0] ?? ''}
          size={avatarSize}
          src={avatarSrc[0]}
        />,
        <Avatar
          key="second"
          alt={avatarNames?.[1] ?? ''}
          size={avatarSize}
          src={avatarSrc[1]}
        />,
      ]
    : [
        <Avatar
          key="main"
          alt={label}
          background={
            emoji
              ? 'secondary'
              : (avatarBg as ThemeColorsBackground | ThemeColorsMeta)
          }
          emojiComponent={emoji && <Emoji emoji={emoji} size={12} />}
          shape="circle"
          size={avatarSize}
          src={Array.isArray(avatarSrc) ? avatarSrc[0] : avatarSrc}
        />,
      ];

  const expandOverlay = hasExpandOverlay && (
    <Avatar
      key="expand"
      alt=""
      background="secondary"
      className={styles.fullscreenChip}
      emojiComponent={
        <IconFullscreenDefault
          style={{
            color: 'var(--colors-components-icon-button-primary)',
            height: 10,
            width: 10,
          }}
        />
      }
      shape="circle"
      size={avatarSize}
    />
  );

  const avatars = expandOverlay
    ? [...identityAvatars, expandOverlay]
    : identityAvatars;

  return isStacked ? (
    <AvatarGroup interactive={false}>{avatars}</AvatarGroup>
  ) : (
    avatars[0]
  );
}

export const UserInputToken = ({
  status,
  emoji,
  label,
  avatarBackground,
  avatarSrc,
  avatarNames,
  memberCount,
  isClickable,
  tooltip,
  ...chipProps
}: UserInputTokenProps) => {
  const avatarBg =
    status === InputTokenStatus.GROUP ? 'transparent' : avatarBackground;
  const isValid = status !== InputTokenStatus.INVALID_EMAIL;
  const hasNotice = NOTICE_STATUSES.includes(status);
  const isNew =
    status === InputTokenStatus.NEW_GUEST ||
    status === InputTokenStatus.NEW_MEMBER;

  const canShowAvatar = isValid && !hasNotice && !isNew;

  let color: ChipColors = TOKEN_COLORS.DEFAULT;
  color = hasNotice ? TOKEN_COLORS.NOTICE : color;
  color = !isValid ? TOKEN_COLORS.INVALID : color;

  const avatarSlot = canShowAvatar ? (
    <TokenAvatarSlot
      avatarBg={avatarBg}
      avatarNames={avatarNames}
      avatarSrc={avatarSrc}
      emoji={emoji}
      isClickable={isClickable}
      label={label}
    />
  ) : null;

  let iconBefore: React.ReactNode;
  if (hasNotice) {
    iconBefore = <IconInfo />;
  } else if (isClickable) {
    iconBefore = <IconExpand size={16} />;
  }

  return (
    <Tooltip {...tooltip} disabled={!tooltip}>
      <StyledChip
        $isDefault={color === 'default'}
        avatar={avatarSlot}
        color={color}
        data-testid="user-input-token"
        iconBefore={iconBefore}
        /**
         * Every chip needs to be made `selectable` in order to preserve
         * backspace deletion.
         */
        selectable={true}
        {...chipProps}
      >
        {label}
        {isValid && <StatusText memberCount={memberCount} status={status} />}
      </StyledChip>
    </Tooltip>
  );
};

/**
 * @note This is a workaround for the fact that the latest Chip design
 * have not been implemented in the Chip component in @frameio/vapor.
 * https://figmashort.link/KDNdtr
 */
const StyledChip = styled(Chip)<{ $isDefault: boolean }>`
  > span {
    color: ${theme.colors.text.body};
    font-weight: 500;
  }

  > svg:last-child {
    color: ${theme.colors.icon.primary};
  }

  ${whenFocused} {
    * {
      color: ${(p) =>
        p.$isDefault
          ? theme.colors.components.button.primary.text
          : theme.colors.components.chip.text};
    }
  }
`;
